import React, { useState, useEffect, useRef } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { 
  Ship, 
  Trophy, 
  Download, 
  CheckCircle, 
  XCircle,
  Star,
  Award,
  Zap,
  Waves,
  Anchor,
  Compass,
  MapPin,
  Flag
} from 'lucide-react';

// Types
interface ShipAnimationProps {
  userScore: number;
  opponentScore: number;
  totalQuestions: number;
  won: boolean;
  prize: number;
  captainLogs: string;
  onDownloadLogs: () => void;
  onFinish: () => void;
}

// Animations
const sail = keyframes`
  0% { transform: translateX(-150px) translateY(-8px) rotate(0deg); }
  100% { transform: translateX(85vw) translateY(-8px) rotate(0deg); }
`;

const capsize = keyframes`
  0% { transform: translateX(85vw) translateY(-8px) rotate(0deg) scale(1); }
  25% { transform: translateX(85vw) translateY(0px) rotate(15deg) scale(0.95); }
  50% { transform: translateX(85vw) translateY(10px) rotate(30deg) scale(0.9); }
  75% { transform: translateX(85vw) translateY(15px) rotate(45deg) scale(0.85); }
  100% { transform: translateX(85vw) translateY(20px) rotate(60deg) scale(0.8); }
`;

const dock = keyframes`
  0% { transform: translateX(85vw) translateY(-8px) rotate(0deg) scale(1); }
  50% { transform: translateX(85vw) translateY(-5px) rotate(0deg) scale(1.05); }
  100% { transform: translateX(85vw) translateY(-8px) rotate(0deg) scale(1.1); }
`;

const bob = keyframes`
  0%, 100% { transform: translateY(-8px); }
  50% { transform: translateY(-16px); }
`;

const wave = keyframes`
  0% { transform: translateX(-100%); }
  100% { transform: translateX(100%); }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
`;

const sparkle = keyframes`
  0%, 100% { opacity: 0; transform: scale(0) rotate(0deg); }
  50% { opacity: 1; transform: scale(1) rotate(180deg); }
`;

const icebergFloat = keyframes`
  0%, 100% { transform: translateY(0px) rotate(0deg); }
  50% { transform: translateY(-5px) rotate(1deg); }
`;

const statueFloat = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-8px); }
`;

const harborGlow = keyframes`
  0%, 100% { opacity: 0.6; }
  50% { opacity: 1; }
`;

// Styled Components
const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  color: white;
  position: relative;
  overflow: hidden;
`;

const Background = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, #1e3c72 0%, #2a5298 100%);
  z-index: -2;
`;

const OceanBackground = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 45%;
  background: linear-gradient(180deg, transparent 0%, #006994 50%, #004d73 100%);
  z-index: -1;
`;

const WaveLayer = styled.div<{ delay: number; speed: number }>`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 25px;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.15), transparent);
  animation: ${wave} ${({ speed }) => speed}s infinite linear;
  animation-delay: ${({ delay }) => delay}s;
  opacity: 0.7;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 30px;
  padding: 20px;
  z-index: 1;
`;

const Title = styled.h1`
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 10px;
  background: linear-gradient(45deg, #ffd700, #ffed4e);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const Subtitle = styled.p`
  font-size: 1.2rem;
  color: rgba(255, 255, 255, 0.8);
  margin: 0;
`;

const AnimationContainer = styled.div`
  flex: 1;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 500px;
`;

const ShipContainer = styled.div<{ phase: 'sailing' | 'capsizing' | 'capsized' | 'docking' | 'docked' | 'results' }>`
  position: absolute;
  bottom: 25%;
  left: 0;
  z-index: 10;
  transition: all 0.5s ease;
  
  ${({ phase }) => {
    switch (phase) {
      case 'sailing':
        return css`
          animation: ${sail} 10s linear forwards;
        `;
      case 'capsizing':
        return css`
          animation: ${capsize} 3s ease-in-out forwards;
        `;
      case 'capsized':
        return css`
          transform: translateX(85vw) translateY(20px) rotate(60deg) scale(0.8);
        `;
      case 'docking':
        return css`
          animation: ${dock} 2s ease-in-out forwards;
        `;
      case 'docked':
        return css`
          transform: translateX(85vw) translateY(-8px) rotate(0deg) scale(1.1);
        `;
      case 'results':
        return css`
          transform: translateX(85vw) translateY(-8px) rotate(0deg) scale(1.1);
        `;
      default:
        return css``;
    }
  }}
`;

const RealisticShip = styled.div<{ phase: string }>`
  position: relative;
  width: 120px;
  height: 80px;
  transform: scale(1.2);
  
  /* Ship Hull */
  &::before {
    content: '';
    position: absolute;
    bottom: 0;
    left: 20px;
    width: 80px;
    height: 25px;
    background: linear-gradient(180deg, #8B4513 0%, #654321 100%);
    border-radius: 0 0 40px 40px;
    border: 2px solid #654321;
  }
  
  /* Ship Deck */
  &::after {
    content: '';
    position: absolute;
    bottom: 25px;
    left: 15px;
    width: 90px;
    height: 15px;
    background: linear-gradient(180deg, #DEB887 0%, #CD853F 100%);
    border-radius: 5px;
    border: 2px solid #8B4513;
  }
  
  /* Main Mast */
  .mast {
    position: absolute;
    bottom: 40px;
    left: 55px;
    width: 4px;
    height: 35px;
    background: linear-gradient(180deg, #8B4513 0%, #654321 100%);
    border-radius: 2px;
  }
  
  /* Sail */
  .sail {
    position: absolute;
    bottom: 45px;
    left: 45px;
    width: 25px;
    height: 25px;
    background: linear-gradient(135deg, #F5F5DC 0%, #DEB887 100%);
    border-radius: 0 0 25px 0;
    border: 1px solid #8B4513;
    transform: rotate(-15deg);
  }
  
  /* Flag */
  .flag {
    position: absolute;
    top: 0;
    left: 55px;
    width: 15px;
    height: 12px;
    background: linear-gradient(45deg, #FF0000, #0000FF);
    border-radius: 0 0 8px 0;
    border: 1px solid #000;
  }
  
  /* Windows */
  .windows {
    position: absolute;
    bottom: 30px;
    left: 25px;
    width: 60px;
    height: 8px;
    display: flex;
    gap: 8px;
  }
  
  .window {
    width: 6px;
    height: 6px;
    background: #87CEEB;
    border-radius: 50%;
    border: 1px solid #000;
  }
  
  /* Capsize effect */
  ${({ phase }) => phase === 'capsizing' && css`
    transform: scale(1.2) rotate(45deg);
  `}
  
  ${({ phase }) => phase === 'capsized' && css`
    transform: scale(0.8) rotate(60deg);
  `}
  
  /* Sailing animation */
  ${({ phase }) => phase === 'sailing' && css`
    animation: ${bob} 3s infinite ease-in-out;
  `}
`;

const Iceberg = styled.div<{ isVisible: boolean }>`
  position: absolute;
  bottom: 20%;
  right: 10%;
  width: 80px;
  height: 120px;
  opacity: ${({ isVisible }) => isVisible ? 1 : 0};
  transition: opacity 1s ease;
  z-index: 5;
  
  /* Iceberg base */
  &::before {
    content: '';
    position: absolute;
    bottom: 0;
    left: 10px;
    width: 60px;
    height: 80px;
    background: linear-gradient(180deg, #E6F3FF 0%, #B3D9FF 100%);
    border-radius: 0 0 30px 30px;
    border: 2px solid #99CCFF;
  }
  
  /* Iceberg peak */
  &::after {
    content: '';
    position: absolute;
    bottom: 80px;
    left: 25px;
    width: 30px;
    height: 40px;
    background: linear-gradient(180deg, #FFFFFF 0%, #E6F3FF 100%);
    border-radius: 0 0 15px 15px;
    border: 2px solid #B3D9FF;
  }
  
  animation: ${icebergFloat} 4s infinite ease-in-out;
`;

const Harbor = styled.div<{ isVisible: boolean }>`
  position: absolute;
  bottom: 20%;
  right: 5%;
  width: 200px;
  height: 150px;
  opacity: ${({ isVisible }) => isVisible ? 1 : 0};
  transition: opacity 1s ease;
  z-index: 5;
  
  /* Harbor base */
  &::before {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 200px;
    height: 40px;
    background: linear-gradient(180deg, #8B4513 0%, #654321 100%);
    border-radius: 20px 20px 0 0;
    border: 2px solid #654321;
  }
  
  /* Harbor buildings */
  .building {
    position: absolute;
    bottom: 40px;
    width: 30px;
    height: 60px;
    background: linear-gradient(180deg, #696969 0%, #808080 100%);
    border: 2px solid #000;
    border-radius: 5px 5px 0 0;
  }
  
  .building:nth-child(1) { left: 20px; height: 70px; }
  .building:nth-child(2) { left: 60px; height: 80px; }
  .building:nth-child(3) { left: 100px; height: 65px; }
  .building:nth-child(4) { left: 140px; height: 75px; }
  
  /* Windows */
  .building::after {
    content: '';
    position: absolute;
    top: 10px;
    left: 5px;
    width: 20px;
    height: 8px;
    background: #FFD700;
    border-radius: 2px;
  }
  
  /* Harbor glow */
  animation: ${harborGlow} 3s infinite ease-in-out;
`;

const StatueOfLiberty = styled.div<{ isVisible: boolean }>`
  position: absolute;
  bottom: 20%;
  right: 15%;
  width: 60px;
  height: 120px;
  opacity: ${({ isVisible }) => isVisible ? 1 : 0};
  transition: opacity 1s ease;
  z-index: 6;
  
  /* Base */
  &::before {
    content: '';
    position: absolute;
    bottom: 0;
    left: 10px;
    width: 40px;
    height: 20px;
    background: linear-gradient(180deg, #8B4513 0%, #654321 100%);
    border-radius: 20px 20px 0 0;
    border: 2px solid #654321;
  }
  
  /* Statue body */
  &::after {
    content: '';
    position: absolute;
    bottom: 20px;
    left: 15px;
    width: 30px;
    height: 80px;
    background: linear-gradient(180deg, #FFD700 0%, #FFA500 100%);
    border-radius: 15px 15px 0 0;
    border: 2px solid #FF8C00;
  }
  
  /* Torch */
  .torch {
    position: absolute;
    top: 0;
    left: 20px;
    width: 10px;
    height: 20px;
    background: linear-gradient(180deg, #FF4500 0%, #FF6347 100%);
    border-radius: 5px 5px 0 0;
    border: 1px solid #FF4500;
  }
  
  /* Torch flame */
  .flame {
    position: absolute;
    top: -5px;
    left: 2px;
    width: 6px;
    height: 8px;
    background: linear-gradient(180deg, #FFFF00 0%, #FF4500 100%);
    border-radius: 3px 3px 0 0;
    animation: ${statueFloat} 2s infinite ease-in-out;
  }
`;

const ProgressBar = styled.div`
  position: absolute;
  bottom: 15%;
  left: 10%;
  right: 10%;
  height: 10px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 5px;
  overflow: hidden;
  z-index: 5;
  border: 2px solid rgba(255, 255, 255, 0.3);
`;

const ProgressFill = styled.div<{ progress: number }>`
  height: 100%;
  background: linear-gradient(90deg, #4caf50, #8bc34a);
  border-radius: 3px;
  transition: width 0.5s ease;
  width: ${({ progress }) => progress}%;
  box-shadow: 0 0 10px rgba(76, 175, 80, 0.5);
`;

const ResultsSection = styled.div<{ isVisible: boolean }>`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(20px);
  border-radius: 25px;
  padding: 40px;
  text-align: center;
  border: 2px solid rgba(255, 255, 255, 0.2);
  max-width: 600px;
  width: 90%;
  z-index: 20;
  opacity: ${({ isVisible }) => isVisible ? 1 : 0};
  transform: ${({ isVisible }) => isVisible ? 'translate(-50%, -50%) scale(1)' : 'translate(-50%, -50%) scale(0.8)'};
  transition: all 0.8s ease;
`;

const ResultIcon = styled.div<{ won: boolean }>`
  font-size: 4rem;
  margin-bottom: 20px;
  color: ${({ won }) => won ? '#4caf50' : '#f44336'};
  animation: ${float} 2s infinite ease-in-out;
`;

const ResultTitle = styled.h2<{ won: boolean }>`
  font-size: 2rem;
  font-weight: 700;
  margin-bottom: 15px;
  color: ${({ won }) => won ? '#4caf50' : '#f44336'};
`;

const ScoreDisplay = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 30px;
  margin: 30px 0;
`;

const ScoreCard = styled.div<{ isWinner: boolean }>`
  background: ${({ isWinner }) => isWinner 
    ? 'rgba(76, 175, 80, 0.2)' 
    : 'rgba(255, 255, 255, 0.1)'
  };
  border: 2px solid ${({ isWinner }) => isWinner 
    ? '#4caf50' 
    : 'rgba(255, 255, 255, 0.2)'
  };
  border-radius: 20px;
  padding: 25px;
  text-align: center;
`;

const ScoreLabel = styled.div`
  font-size: 1rem;
  color: rgba(255, 255, 255, 0.8);
  margin-bottom: 10px;
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const ScoreValue = styled.div`
  font-size: 2.5rem;
  font-weight: 700;
  color: white;
`;

const PrizeDisplay = styled.div<{ won: boolean }>`
  background: ${({ won }) => won 
    ? 'linear-gradient(135deg, #4caf50, #8bc34a)' 
    : 'rgba(255, 255, 255, 0.1)'
  };
  border-radius: 20px;
  padding: 25px;
  margin: 20px 0;
  border: 2px solid ${({ won }) => won ? '#4caf50' : 'rgba(255, 255, 255, 0.2)'};
`;

const PrizeAmount = styled.div`
  font-size: 2rem;
  font-weight: 700;
  color: white;
  margin-bottom: 5px;
`;

const PrizeLabel = styled.div`
  font-size: 1rem;
  color: rgba(255, 255, 255, 0.9);
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const CaptainLogsSection = styled.div`
  background: rgba(0, 0, 0, 0.3);
  border-radius: 15px;
  padding: 20px;
  margin: 20px 0;
  border: 1px solid rgba(255, 255, 255, 0.1);
`;

const LogsTitle = styled.h4`
  font-size: 1.1rem;
  font-weight: 600;
  color: #ffd700;
  margin-bottom: 15px;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const LogsContent = styled.pre`
  font-family: 'Courier New', monospace;
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.9);
  line-height: 1.4;
  max-height: 200px;
  overflow-y: auto;
  background: rgba(0, 0, 0, 0.2);
  padding: 15px;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.1);
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 20px;
  justify-content: center;
  margin-top: 30px;
  flex-wrap: wrap;
`;

const ActionButton = styled.button<{ variant: 'primary' | 'secondary' }>`
  background: ${({ variant }) => variant === 'primary' 
    ? 'linear-gradient(45deg, #4caf50, #8bc34a)' 
    : 'rgba(255, 255, 255, 0.1)'
  };
  color: white;
  border: 1px solid ${({ variant }) => variant === 'primary' 
    ? '#4caf50' 
    : 'rgba(255, 255, 255, 0.2)'
  };
  padding: 15px 30px;
  border-radius: 25px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 10px;
  
  &:hover {
    transform: scale(1.05);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.3);
  }
`;

const Sparkle = styled.div<{ delay: number; left: number }>`
  position: absolute;
  top: 20%;
  left: ${({ left }) => left}%;
  width: 6px;
  height: 6px;
  background: #ffd700;
  border-radius: 50%;
  animation: ${sparkle} 2s infinite ease-in-out;
  animation-delay: ${({ delay }) => delay}s;
`;

const ShipAnimation: React.FC<ShipAnimationProps> = ({
  userScore,
  opponentScore,
  totalQuestions,
  won,
  prize,
  captainLogs,
  onDownloadLogs,
  onFinish
}) => {
  const [phase, setPhase] = useState<'sailing' | 'capsizing' | 'capsized' | 'docking' | 'docked' | 'results'>('sailing');
  const [showResults, setShowResults] = useState(false);
  const [progress, setProgress] = useState(0);
  const [showIceberg, setShowIceberg] = useState(false);
  const [showHarbor, setShowHarbor] = useState(false);
  const [showStatue, setShowStatue] = useState(false);
  const [logsDownloaded, setLogsDownloaded] = useState(false);
  const downloadRef = useRef<HTMLAnchorElement>(null);

  // Auto-download captain logs when component mounts
  useEffect(() => {
    if (!logsDownloaded && captainLogs) {
      const timer = setTimeout(() => {
        downloadCaptainLogs();
      }, 2000); // Download after 2 seconds
      
      return () => clearTimeout(timer);
    }
  }, [captainLogs, logsDownloaded]);

  const downloadCaptainLogs = () => {
    if (downloadRef.current && captainLogs) {
      const blob = new Blob([captainLogs], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      downloadRef.current.href = url;
      downloadRef.current.download = `captain-logs-${new Date().toISOString().split('T')[0]}.txt`;
      downloadRef.current.click();
      URL.revokeObjectURL(url);
      setLogsDownloaded(true);
    }
  };

  useEffect(() => {
    // Start sailing animation
    const sailTimer = setTimeout(() => {
      if (won) {
        // Winner: Dock at harbor
        setPhase('docking');
        setShowHarbor(true);
        setShowStatue(true);
        
        setTimeout(() => {
          setPhase('docked');
          
          setTimeout(() => {
            setPhase('results');
            setShowResults(true);
          }, 1000);
        }, 2000);
      } else {
        // Loser: Hit iceberg and capsize
        setShowIceberg(true);
        setPhase('capsizing');
        
        setTimeout(() => {
          setPhase('capsized');
          
          setTimeout(() => {
            setPhase('results');
            setShowResults(true);
          }, 1000);
        }, 3000);
      }
    }, 10000); // 10 seconds sailing time

    // Progress bar animation
    const progressInterval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 100) {
          clearInterval(progressInterval);
          return 100;
        }
        return prev + 1;
      });
    }, 100); // 10 seconds total / 100 steps

    return () => {
      clearTimeout(sailTimer);
      clearInterval(progressInterval);
    };
  }, [won]);

  const getResultTitle = () => {
    if (won) {
      return 'Victory! Welcome to New York Harbor!';
    } else if (userScore === opponentScore) {
      return 'It\'s a Tie!';
    } else {
      return 'Shipwreck! Better Luck Next Time!';
    }
  };

  const getResultIcon = () => {
    if (won) {
      return <Trophy />;
    } else if (userScore === opponentScore) {
      return <Star />;
    } else {
      return <XCircle />;
    }
  };

  const getResultSubtitle = () => {
    if (won) {
      return `Congratulations! You've successfully docked at New York Harbor and won KES ${prize.toLocaleString()}!`;
    } else if (userScore === opponentScore) {
      return 'Your ship made it through the storm, but it was a close call!';
    } else {
      return 'Your ship hit an iceberg and capsized. The competition was too tough this time.';
    }
  };

  return (
    <Container>
      <Background />
      <OceanBackground />
      
      {/* Wave layers */}
      <WaveLayer delay={0} speed={3} />
      <WaveLayer delay={1} speed={4} />
      <WaveLayer delay={2} speed={5} />
      
      {/* Hidden download link for captain logs */}
      <a ref={downloadRef} style={{ display: 'none' }} />
      
      {/* Iceberg for losers */}
      <Iceberg isVisible={showIceberg} />
      
      {/* Harbor and Statue of Liberty for winners */}
      <Harbor isVisible={showHarbor}>
        <div className="building" />
        <div className="building" />
        <div className="building" />
        <div className="building" />
      </Harbor>
      
      <StatueOfLiberty isVisible={showStatue}>
        <div className="torch">
          <div className="flame" />
        </div>
      </StatueOfLiberty>
      
      {/* Sparkles for winner */}
      {won && showResults && (
        <>
          <Sparkle delay={0} left={20} />
          <Sparkle delay={0.5} left={40} />
          <Sparkle delay={1} left={60} />
          <Sparkle delay={1.5} left={80} />
        </>
      )}

      <Header>
        <Title>Ship Evaluation</Title>
        <Subtitle>Your ship is sailing through the competition results...</Subtitle>
      </Header>

      <AnimationContainer>
        <ShipContainer phase={phase}>
          <RealisticShip phase={phase}>
            <div className="mast" />
            <div className="sail" />
            <div className="flag" />
            <div className="windows">
              <div className="window" />
              <div className="window" />
              <div className="window" />
              <div className="window" />
            </div>
          </RealisticShip>
        </ShipContainer>

        <ProgressBar>
          <ProgressFill progress={progress} />
        </ProgressBar>

        {showResults && (
          <ResultsSection isVisible={showResults}>
            <ResultIcon won={won}>
              {getResultIcon()}
            </ResultIcon>
            
            <ResultTitle won={won}>
              {getResultTitle()}
            </ResultTitle>
            
            <p style={{ color: 'rgba(255, 255, 255, 0.8)', marginBottom: '20px' }}>
              {getResultSubtitle()}
            </p>

            <ScoreDisplay>
              <ScoreCard isWinner={userScore > opponentScore}>
                <ScoreLabel>Your Score</ScoreLabel>
                <ScoreValue>{userScore}/{totalQuestions}</ScoreValue>
              </ScoreCard>
              
              <ScoreCard isWinner={opponentScore > userScore}>
                <ScoreLabel>Opponent Score</ScoreLabel>
                <ScoreValue>{opponentScore}/{totalQuestions}</ScoreValue>
              </ScoreCard>
            </ScoreDisplay>

            <PrizeDisplay won={won}>
              <PrizeAmount>
                {won ? `KES ${prize.toLocaleString()}` : 'KES 0'}
              </PrizeAmount>
              <PrizeLabel>
                {won ? 'Prize Won!' : 'No Prize'}
              </PrizeLabel>
            </PrizeDisplay>

            <CaptainLogsSection>
              <LogsTitle>
                <Compass size={20} />
                Captain's Logs
                {logsDownloaded && <CheckCircle size={16} color="#4caf50" />}
              </LogsTitle>
              <LogsContent>{captainLogs}</LogsContent>
              {!logsDownloaded && (
                <p style={{ fontSize: '0.8rem', color: '#4caf50', marginTop: '10px' }}>
                  ⬇️ Captain's logs will be automatically downloaded...
                </p>
              )}
            </CaptainLogsSection>

            <ActionButtons>
              <ActionButton variant="secondary" onClick={downloadCaptainLogs}>
                <Download size={20} />
                {logsDownloaded ? 'Download Again' : 'Download Logs'}
              </ActionButton>
              
              <ActionButton variant="primary" onClick={onFinish}>
                <CheckCircle size={20} />
                Finish
              </ActionButton>
            </ActionButtons>
          </ResultsSection>
        )}
      </AnimationContainer>
    </Container>
  );
};

export default ShipAnimation;
